/**
 * Tipos y funciones para la API de plataforma.
 * Consumidos por la consola de administración /admin/*.
 *
 * Referencias:
 *   - docs/DESIGN_platform_console.md §1.3 (endpoints)
 *   - docs/DESIGN_platform_console_phase4.md §3 (audit)
 *   - apps/worker-api/src/infrastructure/auth/platform-router.ts
 */
import { platformFetch } from "./platform";

// ---------------------------------------------------------------------------
// Tipos de respuesta de la API
// ---------------------------------------------------------------------------

export type PlatformRoleType = "base" | "dynamic";

export interface PlatformRole {
  name: string;
  permissions: Record<string, string[]>;
  type: PlatformRoleType;
  assignable: boolean;
}

export interface PlatformRolesResponse {
  roles: PlatformRole[];
}

export interface PlatformMembership {
  userId: string;
  role: string;
  grantedBy: string | null;
  email: string | null;
  name: string | null;
  /** A4: estos campos llegarán cuando el PR feat/platform-enrich-email-name mergee */
  grantedByEmail?: string | null;
  grantedByName?: string | null;
}

export interface PlatformMembershipsResponse {
  memberships: PlatformMembership[];
}

export type ImpersonationStatus =
  | { impersonating: false }
  | {
      impersonating: true;
      targetUserId: string;
      targetEmail: string | null;
      targetName: string | null;
      startedAt?: string;
      expiresAt?: string;
      defaultTtlSeconds?: number;
    };

// Respuesta del POST /api/platform/impersonate
export interface StartImpersonationResponse {
  impersonating: true;
  targetUserId: string;
  startedAt: string;
  expiresAt: string;
  ttlSeconds: number;
}

// Cuerpo del POST /api/platform/impersonate
export interface StartImpersonationBody {
  targetUserId: string;
  reason?: string | undefined;
  ttlSeconds?: number | undefined;
}

// Códigos de error del worker para POST /api/platform/impersonate
export type ImpersonationErrorCode =
  | "ESCALATION"
  | "FORBIDDEN"
  | "ROLE_NOT_FOUND"
  | "IMPERSONATE_ACTIVE";

// ---------------------------------------------------------------------------
// Mutaciones de roles y membresías (Fase 2 de la consola)
// ---------------------------------------------------------------------------

/**
 * Códigos de error de dominio que el worker emite en las mutaciones de
 * roles/membresías. Espejan `PlatformRoleError["code"]` de
 * `apps/worker-api/src/infrastructure/auth/platform-roles.ts`. La UI mapea
 * cada code a un mensaje específico (ver DESIGN_platform_console_phase2 §3-8).
 *
 * El cliente NO es la autoridad: el editor de permisos previene la escalada en
 * UX, pero el server SIEMPRE re-valida con `assertNoEscalation` y responde
 * `ESCALATION` (403) si falla. Estos codes existen para que la UI muestre el
 * mensaje correcto, no para confiar en la decisión del cliente.
 */
export type PlatformMutationErrorCode =
  | "FORBIDDEN"
  | "ESCALATION"
  | "ROLE_NOT_FOUND"
  | "ROLE_EXISTS"
  | "ROLE_IN_USE"
  | "ROLE_RESERVED"
  | "INVALID_PERMISSION"
  | "IMPERSONATE_ACTIVE";

/** Mapa de permisos `{ recurso: acciones[] }` que viaja en el body. */
export type PermissionsMap = Record<string, string[]>;

// --- Roles ---

export interface CreatePlatformRoleBody {
  name: string;
  permissions: PermissionsMap;
}

export interface UpdatePlatformRoleBody {
  permissions: PermissionsMap;
}

/**
 * Respuesta de POST/PATCH /roles. El worker devuelve `{ name, permissions,
 * type: "dynamic" }` SIN `assignable` (GAP D1 del DESIGN §10): la UI hace
 * `refetchRoles()` tras mutar para recomputar `assignable` de forma consistente
 * en vez de duplicar la lógica acá.
 */
export interface MutatedPlatformRole {
  name: string;
  permissions: PermissionsMap;
  type: "dynamic";
}

// --- Membresías ---

export interface SetMembershipBody {
  role: string;
}

/** Respuesta de PUT /memberships/:userId (la fila tras el upsert). */
export interface MutatedMembership {
  userId: string;
  role: string;
  grantedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * POST /api/platform/roles — crear un rol dinámico.
 * Gate server: `platformRole:["create"]`.
 * Errores de dominio: ROLE_EXISTS (409), INVALID_PERMISSION (422),
 * ESCALATION (403), ROLE_RESERVED (409), FORBIDDEN (403).
 */
export async function createPlatformRole(
  body: CreatePlatformRoleBody,
): Promise<MutatedPlatformRole> {
  const res = await platformFetch("/api/platform/roles", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await toPlatformMutationError(res, "Error al crear el rol");
  }
  return (await res.json()) as MutatedPlatformRole;
}

/**
 * PATCH /api/platform/roles/:name — reemplazar los permisos de un rol dinámico.
 * Gate server: `platformRole:["update"]`.
 * Errores de dominio: ROLE_NOT_FOUND (404), ROLE_RESERVED (409),
 * INVALID_PERMISSION (422), ESCALATION (403), FORBIDDEN (403).
 */
export async function updatePlatformRole(
  name: string,
  body: UpdatePlatformRoleBody,
): Promise<MutatedPlatformRole> {
  const res = await platformFetch(`/api/platform/roles/${encodeURIComponent(name)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await toPlatformMutationError(res, "Error al actualizar el rol");
  }
  return (await res.json()) as MutatedPlatformRole;
}

/**
 * DELETE /api/platform/roles/:name — borrar un rol dinámico.
 * Gate server: `platformRole:["delete"]`. Respuesta 204 sin body.
 * Errores de dominio: ROLE_IN_USE (409), ROLE_RESERVED (409),
 * ROLE_NOT_FOUND (404), FORBIDDEN (403).
 */
export async function deletePlatformRole(name: string): Promise<void> {
  const res = await platformFetch(`/api/platform/roles/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw await toPlatformMutationError(res, "Error al eliminar el rol");
  }
}

/**
 * PUT /api/platform/memberships/:userId — asignar o cambiar (upsert) el rol de
 * plataforma de un usuario.
 * Gate server: `user:["set-role"]`. Bloqueado si target == actor (403 FORBIDDEN).
 * Errores de dominio: ESCALATION (403), FORBIDDEN (403), ROLE_NOT_FOUND (404).
 */
export async function setMembershipRole(userId: string, role: string): Promise<MutatedMembership> {
  const res = await platformFetch(`/api/platform/memberships/${encodeURIComponent(userId)}`, {
    method: "PUT",
    body: JSON.stringify({ role } satisfies SetMembershipBody),
  });
  if (!res.ok) {
    throw await toPlatformMutationError(res, "Error al asignar el rol");
  }
  return (await res.json()) as MutatedMembership;
}

/**
 * DELETE /api/platform/memberships/:userId — revocar el poder de plataforma.
 * Gate server: `user:["set-role"]`. Idempotente (204 aunque no tuviera membership).
 * Bloqueado si target == actor (403 FORBIDDEN).
 */
export async function revokeMembership(userId: string): Promise<void> {
  const res = await platformFetch(`/api/platform/memberships/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw await toPlatformMutationError(res, "Error al revocar la membresía");
  }
}

// ---------------------------------------------------------------------------
// Agregar miembro de plataforma — flujo unificado anti-enumeración (TICKET-009)
// ---------------------------------------------------------------------------

/** Roles asignables desde este flujo — NUNCA superAdmin (ADR 0011, capa app del POST). */
export type InvitablePlatformRole = "studio-admin" | "studio-staff";

export interface CreatePlatformInvitationBody {
  email: string;
  role: InvitablePlatformRole;
}

/**
 * Respuesta de POST /api/platform/invitations. Shape NEUTRO e INDISTINGUIBLE:
 * el server resuelve internamente si el email ya tenía cuenta (asigna rol) o
 * no (crea invitación), pero SIEMPRE devuelve 201 con este mismo shape — sin
 * `code` ni ningún otro campo que permita inferir cuál rama corrió
 * (TICKET-009, cierra un user-enumeration leak con privacidad cruzada entre
 * clientes). Ver `platform-router.ts` — `genericBody`.
 */
export interface CreatePlatformInvitationResponse {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
}

/**
 * Códigos de error de dominio de POST /api/platform/invitations que SIGUEN
 * siendo visibles al operador — ninguno filtra existencia previa del email.
 * `USER_EXISTS` / `INVITATION_PENDING` fueron eliminados a propósito
 * (TICKET-009): antes eran el oráculo de enumeración, ahora se absorben
 * server-side en la respuesta neutra de éxito.
 */
export type PlatformInvitationErrorCode = "SUPERADMIN_CLI_ONLY" | "UNKNOWN_ROLE";

/**
 * POST /api/platform/invitations — flujo único "Agregar miembro" (TICKET-009).
 * Recibe `{ email, role }`; el server decide server-side si asigna rol a un
 * usuario existente o crea una invitación, sin transparentar cuál ocurrió.
 * Gate server: `user:["set-role"]`.
 * Errores de dominio visibles: 403 SUPERADMIN_CLI_ONLY, 422 UNKNOWN_ROLE (o
 * email/role malformado, sin `code`). Ninguno depende de si el email existía.
 */
export async function createPlatformInvitation(
  body: CreatePlatformInvitationBody,
): Promise<CreatePlatformInvitationResponse> {
  const res = await platformFetch("/api/platform/invitations", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await toPlatformInvitationError(res);
  }
  return (await res.json()) as CreatePlatformInvitationResponse;
}

// ---------------------------------------------------------------------------
// Invitaciones pendientes — listar / reenviar / cancelar (TICKET-004, ADR 0012)
// ---------------------------------------------------------------------------

/**
 * Estados de una invitación de plataforma que `GET /invitations` puede
 * devolver. Solo `pending` y `expired` (el endpoint filtra `accepted`/
 * `cancelled` — son terminales, fuera del panel operativo).
 * Espeja el CHECK `platformInvitation_status_check` de schema.ts.
 */
export type PlatformInvitationStatus = "pending" | "expired";

/** Una fila del listado de `GET /api/platform/invitations`. */
export interface PlatformInvitation {
  id: string;
  email: string;
  role: string;
  status: PlatformInvitationStatus;
  invitedBy: string;
  inviterName: string | null;
  inviterEmail: string | null;
  /** ISO 8601 UTC */
  createdAt: string;
  /** ISO 8601 UTC */
  expiresAt: string;
  /**
   * Si el envío por Brevo se confirmó. Nullable: columna agregada en la
   * migración `0020`, así que las filas previas no tienen el dato — `null`
   * es "desconocido", NO "falló" (ver `describeDeliveryStatus` en
   * `PendingInvitationsSection.tsx`).
   */
  emailDelivered: boolean | null;
}

export interface PlatformInvitationsResponse {
  invitations: PlatformInvitation[];
}

/**
 * GET /api/platform/invitations — lista invitaciones "abiertas" (pending +
 * expired). Gate server: `user:["set-role"]`. El worker hace lazy-expire
 * (marca `expired` las `pending` vencidas) antes de responder — no hay
 * housekeeping del lado del cliente.
 */
export async function listPlatformInvitations(): Promise<PlatformInvitation[]> {
  const res = await platformFetch("/api/platform/invitations");
  if (!res.ok) {
    throw new ApiError(res.status, "Error al cargar las invitaciones pendientes");
  }
  const data = (await res.json()) as PlatformInvitationsResponse;
  return data.invitations;
}

/** Respuesta de POST /api/platform/invitations/:id/resend (happy path). */
export interface ResendPlatformInvitationResponse {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
}

/**
 * Códigos de error de dominio de resend/cancel. Espejan los `code` exactos
 * emitidos por `platform-router.ts` (`POST /invitations/:id/resend` y
 * `POST /invitations/:id/cancel`, TICKET-003/006/008).
 */
export type PlatformInvitationActionErrorCode =
  | "INVITATION_NOT_FOUND"
  | "SUPERADMIN_CLI_ONLY"
  | "INVITATION_EXPIRED"
  | "RESEND_ALREADY_USED"
  | "EMAIL_NOT_SENT"
  | "EMAIL_DELIVERY_FAILED_AFTER_RESERVE";

/**
 * POST /api/platform/invitations/:id/resend — reenvía una `pending` no
 * vencida que todavía no consumió su único resend. Gate: `user:["set-role"]`.
 *
 * Errores de dominio: 404 INVITATION_NOT_FOUND (anti-oráculo: no existe o no
 * está pending), 403 SUPERADMIN_CLI_ONLY, 409 INVITATION_EXPIRED/
 * RESEND_ALREADY_USED, 502 EMAIL_NOT_SENT/EMAIL_DELIVERY_FAILED_AFTER_RESERVE
 * (la fila puede haber mutado igual — ver docstring del endpoint).
 */
export async function resendPlatformInvitation(
  id: string,
): Promise<ResendPlatformInvitationResponse> {
  const res = await platformFetch(`/api/platform/invitations/${encodeURIComponent(id)}/resend`, {
    method: "POST",
  });
  if (!res.ok) {
    throw await toPlatformInvitationActionError(res, "Error al reenviar la invitación");
  }
  return (await res.json()) as ResendPlatformInvitationResponse;
}

/** Respuesta de POST /api/platform/invitations/:id/cancel. */
export interface CancelPlatformInvitationResponse {
  id: string;
  status: "cancelled";
}

/**
 * POST /api/platform/invitations/:id/cancel — cancela una `pending` (marca
 * `status='cancelled'`). Gate: `user:["set-role"]`.
 *
 * Errores de dominio: 404 INVITATION_NOT_FOUND (anti-oráculo: no existe o no
 * está pending), 403 SUPERADMIN_CLI_ONLY.
 */
export async function cancelPlatformInvitation(
  id: string,
): Promise<CancelPlatformInvitationResponse> {
  const res = await platformFetch(`/api/platform/invitations/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) {
    throw await toPlatformInvitationActionError(res, "Error al cancelar la invitación");
  }
  return (await res.json()) as CancelPlatformInvitationResponse;
}

async function toPlatformInvitationActionError(
  res: Response,
  fallback: string,
): Promise<PlatformInvitationActionError> {
  const payload = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  return new PlatformInvitationActionError(
    res.status,
    payload.error ?? fallback,
    payload.code as PlatformInvitationActionErrorCode | undefined,
  );
}

async function toPlatformInvitationError(res: Response): Promise<PlatformInvitationError> {
  const payload = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  return new PlatformInvitationError(
    res.status,
    payload.error ?? "Error al crear la invitación",
    payload.code as PlatformInvitationErrorCode | undefined,
  );
}

/**
 * Construye un `PlatformMutationError` a partir de una Response no-ok.
 * Lee el body `{ error?, code? }` del worker (handlePlatformError emite ambos
 * para errores de dominio; las validaciones planas solo `error`). Si no hay
 * body parseable, cae al `fallback`. Espeja el patrón de `startImpersonation`.
 */
async function toPlatformMutationError(
  res: Response,
  fallback: string,
): Promise<PlatformMutationError> {
  const payload = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  return new PlatformMutationError(
    res.status,
    payload.error ?? fallback,
    payload.code as PlatformMutationErrorCode | undefined,
  );
}

// ---------------------------------------------------------------------------
// Helper: mostrar "otorgado por" de forma tolerante (A4)
// ---------------------------------------------------------------------------

export function resolveGrantedBy(membership: PlatformMembership): string {
  return membership.grantedByEmail ?? membership.grantedByName ?? membership.grantedBy ?? "—";
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// A5 — GET /api/platform/me
// ---------------------------------------------------------------------------

/**
 * Ámbito de plataforma del actor (ADR 0008 §8 + ADR 0013).
 *
 * `mode: "global"` → superAdmin/studio-admin (por NOMBRE de rol, ADR 0013).
 * Ve/gestiona todo; `assignments` siempre vacío (no depende de filas).
 *
 * `mode: "scoped"` → studio-staff o cualquier rol dinámico. Una entrada por
 * cada `platformOrgAssignment` VIGENTE, con el rol por-cliente y su mapa de
 * permisos ya resuelto (`permissionsSnapshot`) para anotar la UI sin una
 * segunda ronda de requests. `permissionsSnapshot: null` → rol por-cliente
 * corrupto/borrado (la org sigue listada, pero sin permisos resueltos —
 * tratar como "sin poder" en la UI).
 */
export type PlatformScope =
  | { mode: "global"; assignments: [] }
  | {
      mode: "scoped";
      assignments: Array<{
        orgId: string;
        roleName: string;
        permissionsSnapshot: PermissionsMap | null;
      }>;
    };

export interface PlatformMeResponse {
  role: string;
  permissions: Record<string, string[]>;
  scope: PlatformScope;
}

/**
 * GET /api/platform/me — devuelve el rol, permisos y ámbito (ADR 0008 §8) del
 * ACTOR autenticado. Informativo únicamente: el server SIEMPRE re-chequea
 * cada mutación — este payload nunca es la fuente de autorización.
 * Gate: cualquier platform member (membership válida).
 */
export async function fetchPlatformMe(): Promise<PlatformMeResponse> {
  const res = await platformFetch("/api/platform/me");
  if (!res.ok) {
    throw new ApiError(res.status, "Error al cargar perfil de plataforma");
  }
  return (await res.json()) as PlatformMeResponse;
}

export async function fetchPlatformRoles(): Promise<PlatformRole[]> {
  const res = await platformFetch("/api/platform/roles");
  if (!res.ok) {
    throw new ApiError(res.status, "Error al cargar roles");
  }
  const data = (await res.json()) as PlatformRolesResponse;
  return data.roles;
}

export async function fetchPlatformMemberships(): Promise<PlatformMembership[]> {
  const res = await platformFetch("/api/platform/memberships");
  if (!res.ok) {
    throw new ApiError(res.status, "Error al cargar miembros");
  }
  const data = (await res.json()) as PlatformMembershipsResponse;
  return data.memberships;
}

export async function fetchImpersonationStatus(): Promise<ImpersonationStatus | null> {
  const res = await platformFetch("/api/platform/impersonate/status");
  if (res.status === 403) {
    // Sin membership de plataforma
    return null;
  }
  if (!res.ok) {
    throw new ApiError(res.status, "Error al verificar estado de impersonación");
  }
  return (await res.json()) as ImpersonationStatus;
}

// ---------------------------------------------------------------------------
// Impersonación — iniciar y detener
// ---------------------------------------------------------------------------

export async function startImpersonation(
  body: StartImpersonationBody,
): Promise<StartImpersonationResponse> {
  const res = await platformFetch("/api/platform/impersonate", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
    };
    throw new ImpersonationApiError(
      res.status,
      payload.error ?? "Error al iniciar impersonación",
      payload.code,
    );
  }
  return (await res.json()) as StartImpersonationResponse;
}

export async function stopImpersonation(): Promise<void> {
  const res = await platformFetch("/api/platform/impersonate/stop", {
    method: "POST",
  });
  // 200 idempotente — ignoramos el body
  if (!res.ok) {
    throw new ApiError(res.status, "Error al detener impersonación");
  }
}

// ---------------------------------------------------------------------------
// Auditoría — GET /api/platform/audit (Fase 4)
// ---------------------------------------------------------------------------

/**
 * Enum cerrado de los 8 literales de SecurityAuditAction conocidos.
 * Tipado como unión abierta (| string) para no romper si el worker
 * agrega un nuevo literal — el dashboard muestra fallback "Acción: <raw>".
 * Referencia: apps/worker-api/src/infrastructure/auth/audit.ts:36-59
 */
export type AuditAction =
  | "impersonate_start"
  | "impersonate_stop"
  | "platform_membership_assign"
  | "platform_membership_revoke"
  | "platform_role_create"
  | "platform_role_update"
  | "platform_role_delete"
  | "tenant_access_during_impersonation"
  | string; // unión abierta: si el worker agrega un literal nuevo, no rompe

/**
 * Una entrada del log de auditoría.
 *
 * Los campos actorEmail/actorName/targetEmail/targetName son opcionales:
 * el worker los agregará via LEFT JOIN (GAP E1, aún en construcción por @CTO-next).
 * La UI los muestra si llegan; si no, cae a UUID truncado + tooltip + botón copy.
 * Patrón idéntico a resolveGrantedBy de Fase 1.
 */
export interface AuditEntry {
  id: string;
  actorUserId: string;
  targetUserId: string | null;
  /** Unión abierta — ver AuditAction */
  action: AuditAction;
  orgId: string | null;
  /** jsonb — shape varía por action. Tratar como untrusted PII. */
  detail: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  /** ISO 8601 UTC (worker serializa con toISOString) */
  at: string;
  // GAP E1: campos opcionales de enriquecimiento — llegan cuando @CTO-next
  // agregue LEFT JOIN sobre user en platform-router.ts:509-553
  actorEmail?: string | null;
  actorName?: string | null;
  targetEmail?: string | null;
  targetName?: string | null;
}

export interface AuditQuery {
  limit?: number | undefined;
  cursor?: string | undefined;
  action?: AuditAction | undefined;
  actorUserId?: string | undefined;
  targetUserId?: string | undefined;
  /**
   * Filtro por organización (TICKET-010). Activa el gate POR-CLIENTE del
   * worker en vez del gate GLOBAL — ver docstring de `fetchAuditEntries`.
   */
  orgId?: string | undefined;
}

export interface AuditResponse {
  entries: AuditEntry[];
  /** null → no hay más páginas */
  nextCursor: string | null;
}

/**
 * GET /api/platform/audit — lista del securityAuditLog paginado.
 *
 * Gate server (dos caminos, ver `platform-router.ts` handler `GET /audit`):
 *   - SIN `orgId`: `audit:["read"]` GLOBAL — hoy solo superAdmin, o
 *     studio-admin promovido a rol dinámico global con ese verbo.
 *   - CON `orgId`: verbo delegado en cadena — pasa quien tenga el verbo
 *     GLOBAL (rama de arriba) O un actor scoped con asignación vigente en
 *     esa org cuyo rol por-cliente incluye `audit:["read"]` (decisión
 *     producto 2026-08-14). "Global-por-nombre" (studio-admin base) da
 *     ÁMBITO pero NO el verbo — el server NO lo deja pasar solo por eso.
 *
 * El server SIEMPRE re-valida — este cliente no es la autoridad. El 403 es
 * un CASO ESPERADO del negocio (mayoría de los actores no posee el verbo),
 * no una falla: `AuditView` (vista global) lo maneja con página completa;
 * `TenantAuditTab` (vista por-cliente, TICKET-010) lo trata inline como
 * estado sereno de "sin acceso" — nunca como error rojo.
 *
 * Errores:
 *   403 → sin permiso (ver arriba)
 *   422 → cursor inválido/expirado, u `orgId` vacío
 */
export async function fetchAuditEntries(q: AuditQuery): Promise<AuditResponse> {
  const params = new URLSearchParams();
  if (q.limit !== undefined) params.set("limit", String(q.limit));
  if (q.cursor) params.set("cursor", q.cursor);
  if (q.action) params.set("action", q.action);
  if (q.actorUserId) params.set("actorUserId", q.actorUserId);
  if (q.targetUserId) params.set("targetUserId", q.targetUserId);
  if (q.orgId) params.set("orgId", q.orgId);

  const qs = params.toString();
  const res = await platformFetch(`/api/platform/audit${qs ? `?${qs}` : ""}`);

  if (res.status === 403) {
    throw new ApiError(403, "No tenés acceso al log de auditoría");
  }
  if (res.status === 422) {
    const payload = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AuditCursorError(payload.error ?? "Cursor de paginación inválido");
  }
  if (!res.ok) {
    throw new ApiError(res.status, "Error al cargar el log de auditoría");
  }
  return (await res.json()) as AuditResponse;
}

/**
 * Error específico para cursor inválido (422).
 * AuditView lo detecta por instanceof para mostrar el banner de reset.
 */
export class AuditCursorError extends Error {
  readonly type = "invalid_cursor" as const;
  constructor(message: string) {
    super(message);
    this.name = "AuditCursorError";
  }
}

// ---------------------------------------------------------------------------
// Clasificación de errores de fetchAuditEntries — compartida entre AuditView
// (global) y TenantAuditTab (por-cliente, TICKET-010). Función pura: mapea la
// excepción lanzada a un estado de UI. El 403 se modela como dato de estado
// ("forbidden"), no como excepción a alarmar — es el caso esperado de la
// delegación en cadena de `audit:read` (ver fetchAuditEntries arriba).
// ---------------------------------------------------------------------------

export type AuditPageErrorType = "forbidden" | "invalid_cursor" | "network" | "unknown";

export interface AuditPageError {
  type: AuditPageErrorType;
  message: string;
}

export function classifyAuditFetchError(err: unknown): AuditPageError {
  if (err instanceof AuditCursorError) {
    return { type: "invalid_cursor", message: err.message };
  }
  if (err instanceof ApiError && err.status === 403) {
    return { type: "forbidden", message: err.message };
  }
  if (err instanceof Error) {
    return { type: "network", message: err.message };
  }
  return { type: "unknown", message: "Error inesperado al cargar el log" };
}

/**
 * Combina una página nueva de entries con las ya cargadas en pantalla.
 * `isLoadingMore` true → "Cargar más" (concatena, paginación por cursor);
 * false → fetch nuevo por carga inicial o cambio de filtro (reemplaza).
 */
export function mergeAuditPage(
  prevEntries: AuditEntry[],
  incoming: AuditEntry[],
  isLoadingMore: boolean,
): AuditEntry[] {
  return isLoadingMore ? [...prevEntries, ...incoming] : incoming;
}

// ---------------------------------------------------------------------------
// Error tipado
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** Error de impersonación con code semántico del worker. */
export class ImpersonationApiError extends ApiError {
  constructor(
    status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(status, message);
    this.name = "ImpersonationApiError";
  }
}

/**
 * Error de mutación de roles/membresías con `code` de dominio.
 * La UI usa `code` para mapear un mensaje específico (ROLE_EXISTS → "Ya existe
 * un rol con ese nombre", etc.) y `status`/`message` como fallback.
 * Mismo patrón que `ImpersonationApiError`.
 */
export class PlatformMutationError extends ApiError {
  constructor(
    status: number,
    message: string,
    public readonly code?: PlatformMutationErrorCode,
  ) {
    super(status, message);
    this.name = "PlatformMutationError";
  }
}

/**
 * Error de POST /api/platform/invitations con `code` de dominio.
 * Mismo patrón que `PlatformMutationError`, codes propios del endpoint de
 * invitación (ver `PlatformInvitationErrorCode`).
 */
export class PlatformInvitationError extends ApiError {
  constructor(
    status: number,
    message: string,
    public readonly code?: PlatformInvitationErrorCode,
  ) {
    super(status, message);
    this.name = "PlatformInvitationError";
  }
}

/**
 * Error de resend/cancel de invitaciones con `code` de dominio.
 * Mismo patrón que `PlatformInvitationError`, codes propios de esos dos
 * endpoints (ver `PlatformInvitationActionErrorCode`).
 */
export class PlatformInvitationActionError extends ApiError {
  constructor(
    status: number,
    message: string,
    public readonly code?: PlatformInvitationActionErrorCode,
  ) {
    super(status, message);
    this.name = "PlatformInvitationActionError";
  }
}
